// frontend/src/components/Dashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout'; // Import the shared Layout component
import { FaGamepad } from 'react-icons/fa'; // Icon for Game Users tile

// Read Backend URL from environment variable - CRITICAL for deployment
const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL;
if (!BACKEND_API_URL) {
    // Log a critical error if the environment variable is missing.
    console.error("CRITICAL CONFIG ERROR: REACT_APP_BACKEND_API_URL environment variable is not set.");
}

/**
 * Renders the main dashboard page for authenticated users.
 * Fetches user data and linked game usernames upon mounting.
 * Displays this information within the shared Layout.
 * Handles loading states and redirects unauthenticated users to the login page.
 * @returns {React.ReactElement} The rendered Dashboard component.
 */
function Dashboard() {
  // State for authenticated user's data
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // Combined loading for initial user fetch

  // State for linked game users
  const [linkedGameUsers, setLinkedGameUsers] = useState([]);
  const [loadingLinkedUsers, setLoadingLinkedUsers] = useState(true);
  const [linkedUsersError, setLinkedUsersError] = useState(null);

  // React Router hook for programmatic navigation
  const navigate = useNavigate();

  /**
   * Fetches the main authenticated user's data from the backend.
   */
  const fetchUser = useCallback(async () => {
    if (!BACKEND_API_URL) {
        console.error("[Dashboard - User] Cannot fetch user data: REACT_APP_BACKEND_API_URL is not configured.");
        setLoading(false); // Stop initial loading
        navigate('/login'); // Redirect if backend URL is missing
        return;
    }

    setLoading(true); // Start initial loading
    try {
      const apiUrl = `${BACKEND_API_URL}/auth/me`;
      console.log(`[Dashboard - User] Fetching user data from: ${apiUrl}`);
      const response = await fetch(apiUrl, { credentials: 'include' });

      if (!response.ok) {
        if (response.status === 401) {
          console.log('[Dashboard - User] User not authenticated (401).');
        } else {
          console.error(`[Dashboard - User] Error fetching user data: ${response.status} ${response.statusText}`);
        }
        throw new Error(`Authentication check failed with status: ${response.status}`);
      }

      const responseData = await response.json();
      if (responseData.success && responseData.user) {
          setUser(responseData.user);
          console.log('[Dashboard - User] User data received:', responseData.user);
      } else {
           console.error('[Dashboard - User] Backend response error or missing user data. Response:', responseData);
           throw new Error('Invalid user data format received from server.');
      }
    } catch (error) {
      console.error("[Dashboard - User] Auth check/fetch failed:", error.message);
      navigate('/login');
    } finally {
      setLoading(false); // Finish initial loading
    }
  }, [navigate]);

  /**
   * Fetches linked game usernames for the authenticated user.
   */
  const fetchLinkedGameUsers = useCallback(async () => {
    // Ensure user data is available and backend URL is configured
    if (!user || !BACKEND_API_URL) {
        if (!user) console.log("[Dashboard - LinkedUsers] User data not yet available, skipping fetch of linked users.");
        else console.error("[Dashboard - LinkedUsers] Cannot fetch linked users: REACT_APP_BACKEND_API_URL is not configured.");
        setLoadingLinkedUsers(false);
        return;
    }

    setLoadingLinkedUsers(true);
    setLinkedUsersError(null);
    try {
        const apiUrl = `${BACKEND_API_URL}/api/user/linked-game-accounts`;
        console.log(`[Dashboard - LinkedUsers] Fetching linked game users from: ${apiUrl}`);
        const response = await fetch(apiUrl, { credentials: 'include' });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: `HTTP error ${response.status}` }));
            throw new Error(errorData.message || `Failed to fetch linked game users: ${response.status}`);
        }

        const responseData = await response.json();
        if (responseData.success && Array.isArray(responseData.gameUsernames)) {
            setLinkedGameUsers(responseData.gameUsernames);
            console.log('[Dashboard - LinkedUsers] Linked game users received:', responseData.gameUsernames);
        } else {
            throw new Error(responseData.message || 'Invalid linked game users data format received from server.');
        }
    } catch (error) {
        console.error("[Dashboard - LinkedUsers] Fetch failed:", error.message);
        setLinkedUsersError(error.message);
        setLinkedGameUsers([]);
    } finally {
        setLoadingLinkedUsers(false);
    }
  }, [user]); // Depends on 'user' state

  // Effect for initial user data fetch
  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Effect to fetch linked game users once user data is available
  useEffect(() => {
    if (user) { // Only fetch if user is loaded
        fetchLinkedGameUsers();
    }
  }, [user, fetchLinkedGameUsers]);

  // --- Conditional Rendering for Loading/Unauthenticated States ---
  if (loading) { // Initial loading for user data
    return (
      <Layout>
        <div className="flex justify-center items-center h-full">
          Loading dashboard...
        </div>
      </Layout>
    );
  }

  if (!user) { // If user fetch failed or user is null after loading
     return (
        <Layout>
          <div className="flex justify-center items-center h-full">
            Verifying authentication...
          </div>
        </Layout>
     );
  }

  // --- Render Dashboard Content ---
  return (
    <Layout user={user}>
      <div className="max-w-4xl mx-auto text-center p-4 md:p-0">
        <h1 className="text-3xl font-bold mb-6">Welcome to Exodus!</h1>

        {/* Grid for Dashboard Tiles - Adjust to md:grid-cols-1 if only one tile remains, or keep for future tiles */}
        <div className="grid md:grid-cols-1 gap-6 mb-6 text-left">
            {/* Game Users Tile */}
            <div className="bg-gray-800 bg-opacity-75 p-6 rounded-lg shadow-md">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <FaGamepad className="mr-2" /> Game Users
              </h2>
              {loadingLinkedUsers && <p className="text-gray-400">Loading game users...</p>}
              {linkedUsersError && <p className="text-red-400">Error: {linkedUsersError}</p>}
              {!loadingLinkedUsers && !linkedUsersError && (
                linkedGameUsers.length > 0 ? (
                  <ul className="list-disc list-inside text-gray-200 space-y-1">
                    {linkedGameUsers.map((gameUser, index) => (
                      <li key={index}>{gameUser}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-400">No game users linked to this Discord account.</p>
                )
              )}
            </div>
        </div>
        {/* Additional dashboard content can be added here */}
      </div>
    </Layout>
  );
}

export default Dashboard;
